import type { DetectionState, Tag } from '@loczi/api'
import { currentLocale, formatRelative, type SupportedLocale } from '@loczi/i18n'

/**
 * Lógica de apresentação compartilhada entre Web e PWA (§12).
 * Nunca afirmar mais do que os dados sustentam (§4.6).
 *
 * Os textos saem do vue-i18n via `setLabelResolver()` (instalado no
 * bootstrap de cada app). Sem resolver instalado — testes, scripts —
 * caem nos literais pt-BR, que são o locale-fonte do produto.
 */

type LabelResolver = (key: string, params?: Record<string, unknown>) => string
let resolve: LabelResolver | null = null

export function setLabelResolver(fn: LabelResolver | null): void {
  resolve = fn
}

function lbl(key: string, fallback: string, params?: Record<string, unknown>): string {
  return resolve?.(key, params) ?? fallback
}

/** "Última detecção há 12 minutos" — valor calculado, sem afirmar que continua lá. */
export function formatLastSeen(detectedAt: string | null | undefined): string {
  if (!detectedAt) return lbl('devices.last_seen_never', 'Nunca detectada')

  const minutes = Math.round((Date.now() - new Date(detectedAt).getTime()) / 60000)
  if (minutes < 1) return lbl('devices.last_seen_now', 'Última detecção agora mesmo')
  const time = formatRelative(detectedAt, currentLocale())
  return lbl('devices.last_seen', `Última detecção ${time}`, { time })
}

export function detectionLabel(state: DetectionState): string {
  const fallbacks: Record<DetectionState, string> = {
    never_detected: 'Nunca detectada',
    recent: 'Detecção recente',
    stale: 'Desatualizada',
    unknown: 'Sem dados',
  }
  return lbl(`enums.detection.${state}`, fallbacks[state])
}

export function detectionTone(state: DetectionState): 'success' | 'warning' | 'neutral' {
  const tones: Record<DetectionState, 'success' | 'warning' | 'neutral'> = {
    never_detected: 'neutral',
    recent: 'success',
    stale: 'warning',
    unknown: 'neutral',
  }
  return tones[state]
}

export function activationLabel(state: Tag['activation_state']): string {
  const fallbacks: Record<Tag['activation_state'], string> = {
    not_activated: 'Não ativada',
    provisioning: 'Em provisionamento',
    active: 'Ativada',
    failed: 'Falha na ativação',
    revoked: 'Revogada',
  }
  return lbl(`enums.activation.${state}`, fallbacks[state])
}

export function categoryLabel(category: Tag['category'], fallback?: string | null): string {
  const fallbacks: Record<string, string> = {
    pet: 'Pet',
    vehicle: 'Veículo',
    object: 'Objeto',
    asset: 'Ativo',
  }
  const key = category ?? 'object'
  return lbl(`enums.category.${key}`, fallbacks[key] ?? fallback ?? '—')
}

export function formatBRL(cents: number): string {
  return new Intl.NumberFormat(currentLocale(), { style: 'currency', currency: 'BRL' }).format(cents / 100)
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Intl.DateTimeFormat(currentLocale(), {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso))
}

/** Estados de atualização para filtro básico (§12.2) — sem "em movimento"/"online". */
export function updateFilters(): ReadonlyArray<{ value: string; label: string }> {
  return [
    { value: '', label: lbl('devices.filter_all', 'Todas') },
    { value: 'recent', label: detectionLabel('recent') },
    { value: 'stale', label: detectionLabel('stale') },
    { value: 'never_detected', label: detectionLabel('never_detected') },
  ]
}

/** @deprecated Use `updateFilters()` — nomes localizáveis. */
export const UPDATE_FILTERS = [
  { value: '', label: 'Todas' },
  { value: 'recent', label: 'Detecção recente' },
  { value: 'stale', label: 'Desatualizadas' },
  { value: 'never_detected', label: 'Nunca detectadas' },
] as const

export type { SupportedLocale }
